/**
 * The team profile's Stats panel.
 *
 * Every string here except the heading and the two states below arrives server
 * rendered: the season label, the group label, the rank label, and each
 * statistic's heading and value. `teamStatsLogic.ts` explains where they come
 * from and why nothing is computed on the way here.
 *
 * THIS PANEL FETCHES ITS OWN DATA, and that is the one place it deliberately
 * departs from apps/mobile, which loads the table at screen level in
 * `app/(tabs)/scores/team.tsx`. `packages/ui/src/Tabs/Tabs.tsx` renders each
 * panel inside a Radix `Tabs.Content` with no `forceMount`, so an inactive
 * panel is unmounted and this hook does not run until a fan opens the tab. A
 * whole league table is read to print about five numbers, so not reading it for
 * fans who never open Stats is worth the seam. Mobile has no equivalent seam.
 * `features/poolStandings/PoolStandingsPane.tsx` is the existing web precedent
 * for a panel that owns its own read.
 *
 * The rank sits at the top under its own group's name rather than being folded
 * into a sentence. "3rd" and "AFC West" are two separate facts on the wire, and
 * joining them into "3rd in AFC West" would be this app writing copy for a
 * league whose table it does not own. The header above already carries the
 * server's own composed form in `standingLabel`.
 *
 * Primary and secondary statistics render as one list, in the order the season
 * declared its columns, primaries first. They are not visually ranked against
 * each other: a league calls at most three columns primary so a narrow
 * standings table can drop the rest, which is a table layout decision and not a
 * statement that the other numbers matter less to somebody reading one team.
 *
 * Below the standings facts sits `TeamSeasonRanksCard` (ISSUE-1385): the
 * team's season stats from sports-api POST /team/season-stats-get, each with
 * its league rank, in offense and defense sections. It reads on its own and
 * collapses a failure to nothing, the same as the standings read here.
 *
 * The list is a `<dl>`. Each heading and its value are a `<dt>` and `<dd>`
 * pair, so the association a sighted reader gets from the layout is carried in
 * the markup as well. Left as two sibling spans, a screen reader reads "W" and
 * then "7" as unrelated things, and a bare "W" is not a word. apps/mobile
 * solves the same problem with an `accessible` view and a composed
 * `accessibilityLabel`, because React Native has no description list.
 */
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  TEAM_STATS_LOADING_LABEL,
  TEAM_STATS_TITLE,
  TEAM_STATS_UNAVAILABLE_LABEL,
  type typeTeamStatRow,
} from "./teamStatsLogic";
import { TeamSeasonRanksCard } from "./TeamSeasonRanksCard";
import { useTeamSeasonRanks } from "./useTeamSeasonRanks";
import { useTeamStats } from "./useTeamStats";

export type typeTeamStatsTabProps = {
  teamId: string;
  /** From the team profile response. Null when the team has no league. */
  leagueId: string | null;
};

// The standings facts card: rank in its group and the row's columns. When
// the team has no standings row but does have ranked season stats
// (ISSUE-1385), the card steps aside instead of saying stats are missing.
const TeamStandingsStats = (
  props: typeTeamStatsTabProps & { hasRanks: boolean },
): ReactNode => {
  const state = useTeamStats({
    teamId: props.teamId,
    leagueId: props.leagueId,
  });

  if (state.kind === "loading") {
    return (
      <section
        data-testid="team-stats-tab"
        className="rounded-md border border-hairline bg-surface p-4"
      >
        <Skeleton shape="text" lines={5} label={TEAM_STATS_LOADING_LABEL} />
      </section>
    );
  }

  if (state.kind === "absent") {
    if (props.hasRanks) return null;
    return (
      <section
        data-testid="team-stats-tab"
        className="rounded-md border border-hairline bg-surface"
      >
        <h3 className="px-4 pt-3 pb-1 text-caption text-ink-secondary">
          {TEAM_STATS_TITLE}
        </h3>
        <p
          data-testid="team-stats-empty"
          className="px-4 pb-3 text-body text-ink-secondary"
        >
          {TEAM_STATS_UNAVAILABLE_LABEL}
        </p>
      </section>
    );
  }

  const view = state.view;
  const rows: typeTeamStatRow[] = [...view.primary, ...view.secondary];

  return (
    <section
      data-testid="team-stats-tab"
      className="rounded-md border border-hairline bg-surface"
    >
      <h3 className="px-4 pt-3 pb-1 text-caption text-ink-secondary">
        {TEAM_STATS_TITLE}
      </h3>
      <p
        data-testid="team-stats-season"
        className="px-4 pb-2 text-caption text-ink-secondary"
      >
        {view.seasonLabel}
      </p>
      <div
        data-testid="team-stats-rank"
        className="flex flex-wrap items-baseline justify-between gap-2 border-t border-hairline px-4 py-3"
      >
        <span className="text-body text-ink-secondary">{view.groupLabel}</span>
        <span
          data-testid="team-stats-rank-value"
          className="text-body-strong text-ink tabular-nums"
        >
          {view.rankLabel}
        </span>
      </div>
      <dl className="m-0">
        {rows.map((row) => (
          <div
            key={row.key}
            data-testid={`team-stat-row-${row.key}`}
            className="flex flex-wrap items-baseline justify-between gap-2 border-t border-hairline px-4 py-3"
          >
            <dt className="text-body text-ink-secondary">{row.label}</dt>
            <dd
              data-testid={`team-stat-value-${row.key}`}
              className="m-0 text-body text-ink tabular-nums"
            >
              {row.display}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export const TeamStatsTab = (props: typeTeamStatsTabProps): ReactNode => {
  const ranks = useTeamSeasonRanks(props.teamId);
  const ranksCard =
    ranks.kind === "ready" ? (
      <TeamSeasonRanksCard sections={ranks.sections} />
    ) : ranks.kind === "loading" ? (
      <div data-testid="team-season-ranks-loading">
        <Skeleton shape="card" label={TEAM_STATS_LOADING_LABEL} />
      </div>
    ) : null;
  const standings = (
    <TeamStandingsStats
      teamId={props.teamId}
      leagueId={props.leagueId}
      hasRanks={ranks.kind === "ready"}
    />
  );
  return (
    <div className="flex flex-col gap-4">
      {standings}
      {ranksCard}
    </div>
  );
};
